/**
 * Task 204 — live text-selection geometry, scoped to a container.
 *
 * On the spec's "pretext" question: the robust browser API for measuring the
 * exact on-screen geometry of a *text* selection (not an element) is the DOM
 * Range. `Range.getBoundingClientRect()` gives the whole selection's box (used to
 * place the contextual menu), and `Range.getClientRects()` returns one rect per
 * line fragment — its LAST rect's top-right is the precise landing target for the
 * bookmark flight ("top-right corner of the last highlighted text"). All coords
 * are viewport-relative, which is exactly what a `position: fixed` overlay wants,
 * so the animation lands on the glyphs regardless of scroll. There is no need for
 * canvas text measurement or a hidden "pretext" mirror element — the Range rects
 * are authoritative and cheap.
 *
 * The hook coalesces the noisy `selectionchange` stream with one rAF, ignores
 * selections outside the container, and dismisses on scroll/resize/Escape so the
 * menu never floats over stale coordinates. Normal copy is untouched — we only
 * read the selection, never mutate it.
 */
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

export interface SelectionGeometry {
  /** Whitespace-collapsed selected text. */
  snippet: string;
  /** Nearest enclosing `[data-anchor]` (the chunk), or null. */
  anchor: string | null;
  /** Whole-selection box in viewport coords — for menu placement. */
  bounds: { top: number; left: number; right: number; bottom: number; width: number };
  /** Top-right of the LAST line fragment — the flight landing target. */
  endTopRight: { x: number; y: number };
}

function normalize(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

export interface TextSelectionApi {
  selection: SelectionGeometry | null;
  /** Hide the menu for the current selection (stays hidden until it changes). */
  dismiss: () => void;
}

export function useTextSelection(containerRef: RefObject<HTMLElement>): TextSelectionApi {
  const [selection, setSelection] = useState<SelectionGeometry | null>(null);
  // When the user dismisses, remember the exact text so the same selection does
  // not immediately re-open the menu; a NEW selection clears this and re-shows.
  const dismissedTextRef = useRef<string | null>(null);

  useEffect(() => {
    const compute = (): void => {
      const sel = typeof window !== "undefined" ? window.getSelection() : null;
      if (sel === null || sel.isCollapsed || sel.rangeCount === 0) {
        setSelection(null);
        return;
      }
      const snippet = normalize(sel.toString());
      if (snippet.length === 0) {
        setSelection(null);
        return;
      }
      const container = containerRef.current;
      if (container === null) {
        setSelection(null);
        return;
      }
      const range = sel.getRangeAt(0);
      if (!container.contains(range.commonAncestorContainer)) {
        // Selection is outside the reading pane — not our concern.
        setSelection(null);
        return;
      }
      if (dismissedTextRef.current === snippet) return; // stay hidden for this one
      dismissedTextRef.current = null;

      const rects = range.getClientRects();
      const bounding = range.getBoundingClientRect();
      if (bounding.width === 0 && bounding.height === 0) {
        setSelection(null);
        return;
      }
      const last = rects.length > 0 ? rects[rects.length - 1] : undefined;
      const endTopRight = last !== undefined
        ? { x: last.right, y: last.top }
        : { x: bounding.right, y: bounding.top };

      const endNode = range.endContainer;
      const endEl =
        endNode.nodeType === Node.ELEMENT_NODE
          ? (endNode as Element)
          : endNode.parentElement;
      const anchorEl = endEl?.closest("[data-anchor]") ?? null;
      const anchor = anchorEl?.getAttribute("data-anchor") ?? null;

      setSelection({
        snippet,
        anchor,
        bounds: {
          top: bounding.top,
          left: bounding.left,
          right: bounding.right,
          bottom: bounding.bottom,
          width: bounding.width,
        },
        endTopRight,
      });
    };

    let raf = 0;
    const onSelectionChange = (): void => {
      if (raf !== 0) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(compute);
    };
    const onDismiss = (): void => setSelection(null);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setSelection(null);
    };

    document.addEventListener("selectionchange", onSelectionChange);
    // Capture-phase scroll so any scroll container dismisses the stale menu.
    window.addEventListener("scroll", onDismiss, true);
    window.addEventListener("resize", onDismiss);
    document.addEventListener("keydown", onKey);
    return () => {
      if (raf !== 0) cancelAnimationFrame(raf);
      document.removeEventListener("selectionchange", onSelectionChange);
      window.removeEventListener("scroll", onDismiss, true);
      window.removeEventListener("resize", onDismiss);
      document.removeEventListener("keydown", onKey);
    };
  }, [containerRef]);

  const dismiss = useCallback((): void => {
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    dismissedTextRef.current = sel !== null ? normalize(sel.toString()) : null;
    setSelection(null);
  }, []);

  return { selection, dismiss };
}
